interface ICommercialMotorVehicleOperatorApplication {
  hiringCompany?: any,
  name?: string,
  phone?: string,
  email?: string,
  currentAddress?: string,
  previousAddress?: string,
  dateOfBirth?: string,
  ssn?: string,
  licenses?: ILicense[],
  incidents?: IIncident[],
  records?: IRecord[],
}

interface ILicense {
  state?: string,
  license?: string,
  type?: string,
  expiration?: string,
}

interface IIncident {
  date?: string;
  description?: string;
  injuries?: string;
}

export interface IRecord {
  employer?: string;
  from?: string;
  to?: string;
  address?: string;
  phone?: string;
  supervisor?: string;
  position?: string;
  leavingReason?: string;
}

export const commercialMotorVehicleOperatorApplication = (data: ICommercialMotorVehicleOperatorApplication) => {
  const filterLicenses = (licenses: any[] = []) => {
    return (licenses || [])
      .filter(license => {
        if (!license || typeof license !== 'object') return false;

        const { state, license: licenseNumber, type, expiration } = license;

        return [state, licenseNumber, type, expiration].some(field => field && String(field).trim() !== '');
      })
      .map(license => {
        const { state = '', license: licenseNumber = '', type = '', expiration = '' } = license || {};
        return [state, licenseNumber, type, expiration];
      });
  };

  const filterIncidents = (incidents: any[] = []) => {
    const filtered = (incidents || [])
      .filter(incident => {
        if (!incident || typeof incident !== 'object') return false;
        const { date, description, injuries } = incident;
        return [date, description, injuries].some(field => field && String(field).trim() !== '');
      })
      .map(incident => {
        const { date = '', description = '', injuries = '' } = incident || {};
        return [date, description, injuries];
      });

    return [...filtered, [' ', ' ', ' '], [' ', ' ', ' '], [' ', ' ', ' '],];
  };

  const filterEmploymentRecords = (employmentRecords: any[] = []) => {
    const filtered = (employmentRecords || [])
      .filter(record => {
        if (!record || typeof record !== 'object') return false;

        const { employer, from, to, address, phone, supervisor, position, leavingReason } = record;

        return [employer, from, to, address, phone, supervisor, position, leavingReason]
          .some(field => field && String(field).trim() !== '');
      })
      .map(record => {
        const {
          employer = '',
          from = '',
          to = '',
          address = '',
          phone = '',
          supervisor = '',
          position = '',
          leavingReason = ''
        } = record;

        return [
          employer,
          from,
          to,
          address,
          phone,
          supervisor,
          position,
          leavingReason
        ];
      });

    return [...filtered, ['', '', '', '', '', '', '', '']];
  };

  const generateLicenseQuestion = (record: string[]) => {
    const isFilled = record.some(field => field && String(field).trim() !== '');

    return [
      [
        { text: 'Were you subject to the FMCSRs while employed', colSpan: 2, fontSize: 11, margin: [0, 5, 0, 5], bold: true, border: [false, false, false, false] },
        {}
      ],
      [
        {
          columns: [
            {
              width: 'auto',
              canvas: [
                { type: 'rect', x: 0, y: 0, w: 10, h: 10, lineWidth: 1 },
                ...(isFilled ? [
                  { type: 'line', x1: 1, y1: 5, x2: 4, y2: 8, lineWidth: 1 },
                  { type: 'line', x1: 4, y1: 8, x2: 9, y2: 2, lineWidth: 1 }
                ] : [])
              ]
            },
            { text: 'Yes', fontSize: 11, margin: [5, 0, 20, 0] },
            {
              width: 'auto',
              canvas: [
                { type: 'rect', x: 0, y: 0, w: 10, h: 10, lineWidth: 1 }
                // <-- no check if not filled
              ]
            },
            { text: 'No', fontSize: 11, margin: [5, 0, 0, 0] },
          ],
          columnGap: 5,
          colSpan: 2,
          border: [false, false, false, false],
        },
        {}
      ],
      [
        { text: 'Was your job designated as a safety sensitive function in any DOT regulated mode subject to the Drug & Alcohol testing requirement of 49 CFR Part 40?', bold: true, colSpan: 2, fontSize: 11, margin: [0, 5, 0, 5], border: [false, false, false, false] },
        {}
      ],
      [
        {
          columns: [
            {
              width: 'auto',
              canvas: [
                { type: 'rect', x: 0, y: 0, w: 10, h: 10, lineWidth: 1 },
                ...(isFilled ? [
                  { type: 'line', x1: 1, y1: 5, x2: 4, y2: 8, lineWidth: 1 },
                  { type: 'line', x1: 4, y1: 8, x2: 9, y2: 2, lineWidth: 1 }
                ] : [])
              ]
            },
            { text: 'Yes', fontSize: 11, margin: [5, 0, 20, 0] },
            {
              width: 'auto',
              canvas: [
                { type: 'rect', x: 0, y: 0, w: 10, h: 10, lineWidth: 1 }
              ]
            },
            { text: 'No', fontSize: 11, margin: [5, 0, 0, 0] },
          ],
          columnGap: 5,
          colSpan: 2,
          border: [false, false, false, false],
        },
        {}
      ]
    ];
  };

  return [
    { text: 'Commercial Motor Vehicle Operator Application', style: 'h1' },

    {
      table: {
        widths: ['*', '*'],
        body: [
          [
            { text: `${data.hiringCompany.title}\nUSDOT# ${data.hiringCompany.title === 'Sierra Expediting Inc.' ? '1523843' : '2629893'}`, fontSize: 12, alignment: 'left', border: [false, false, false, false] },
            { text: `${data.hiringCompany.title === 'Sierra Expediting Inc.' ? '101 E Monticello PKWY\nStrafford, MO 65757' : '5517 Ivyridge Ln\nMc Kinney, TX 75071'}\nPhone: (916) 818-0800\nFax: (916) 818-0808`, fontSize: 12, alignment: 'right', border: [false, false, false, false] }
          ]
        ]
      },
      layout: 'noBorders',
    },

    { canvas: [
        // Name line
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 150,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
        // Phone Number
        {
          type: 'line',
          x1: 170,
          y1: 0,
          x2: 320,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
        // E-mail
        {
          type: 'line',
          x1: 340,
          y1: 0,
          x2: 490,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
      ],
      style: 'input',
      margin: [0, 40, 0, 0],
    },

    { text: `${data.name}`, margin: [0,-16, 0, 0]},
    { text: "Name", style: 'label', margin: [0,4,0,0]},
    { text: `${data.phone}`, margin: [170,-30, 0, 0]},
    { text: "Phone Number", style: 'label', margin: [170,4,0,0]},
    { text: `${data.email}`, margin: [340,-30, 0, 0]},
    { text: "E-mail", style: 'label', margin: [340,4,0,0] },

    // Current Address
    { canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 500,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
      ],
      style: 'input',
      margin: [0, 30, 0, 0],
    },

    { text: `${data.currentAddress}`, margin: [0,-16, 0, 0]},
    { text: "Current Address", style: 'label', margin: [0,4,0,0]},

    // Previous Address
    { canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 500,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
      ],
      style: 'input',
      margin: [0, 30, 0, 0],
    },

    { text: `${data.previousAddress || ''}`, margin: [0,-16, 0, 0]},
    { text: "Previous Address", style: 'label', margin: [0,4,0,0]},

    {
      canvas: [
        // Date of Birth
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 200,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
        // SSN
        {
          type: 'line',
          x1: 500,
          y1: 0,
          x2: 350,
          y2: 0,
          lineWidth: 1,
          lineColor: 'black',
        },
      ],
      style: 'input',
      margin: [0, 30, 0, 0],
    },

    { text: `${data.dateOfBirth}`, margin: [0, -16, 0, 0] },
    { text: "Date of Birth", style: 'label', margin: [0, 4, 0, 0] },
    { text: `${data.ssn}`, margin: [350, -30, 0, 0] },
    { text: "Social Security Number", style: 'label', margin: [350, 4, 0, 0] },

    { text: "Driver's License Information", alignment: 'center', bold: true, margin: [0, 20, 0, 0] },

    { table:
        {
          widths: [ '*', '*', '*', '*' ],
          body: [
            [
              { text: 'STATE', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
              { text: 'LICENSE NO.', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
              { text: 'TYPE', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
              { text: 'EXPIRATION DATE', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
            ],
            ...filterLicenses(data.licenses),
          ],
        },
      margin: [0, 10, 0, 0],
    },

    { text: 'Have you ever beed denied a license, permit or privilege to operate a motor vehicle?', fontSize: 11, margin: [0, 20, 0, 0] },
    {
      columns: [
        {
          width: 'auto',
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: 0,
              w: 10,
              h: 10,
              lineWidth: 1,
            },
          ],
        },
        {
          text: 'Yes',
          fontSize: 11,
          margin: [5, 0, 20, 0],
        },
        {
          width: 'auto',
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: 0,
              w: 10,
              h: 10,
              lineWidth: 1,
            },
            {
              type: 'line',
              x1: 1,
              y1: 5,
              x2: 4,
              y2: 8,
              lineWidth: 1,
            },
            {
              type: 'line',
              x1: 4,
              y1: 8,
              x2: 9,
              y2: 2,
              lineWidth: 1,
            },
          ],
        },
        {
          text: 'No',
          fontSize: 11,
          margin: [5, 0, 0, 0],
        },
      ],
      margin: [0, 10, 0, 0],
    },
    { text: 'Has any license, permit or privilege ever been suspended or revoked?', fontSize: 11, margin: [0, 10, 0, 0] },
    {
      columns: [
        {
          width: 'auto',
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: 0,
              w: 10,
              h: 10,
              lineWidth: 1,
            },
          ],
        },
        {
          text: 'Yes',
          fontSize: 11,
          margin: [5, 0, 20, 0],
        },
        {
          width: 'auto',
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: 0,
              w: 10,
              h: 10,
              lineWidth: 1,
            },
            {
              type: 'line',
              x1: 1,
              y1: 5,
              x2: 4,
              y2: 8,
              lineWidth: 1,
            },
            {
              type: 'line',
              x1: 4,
              y1: 8,
              x2: 9,
              y2: 2,
              lineWidth: 1,
            },
          ],
        },
        {
          text: 'No',
          fontSize: 11,
          margin: [5, 0, 0, 0],
        },
      ],
      margin: [0, 10, 0, 0],
    },

    { text: "If you answered 'yes' to either of the above 2 questions, attach a statement of explanation:", fontSize: 11, margin: [0, 10, 0, 0]  },
    {
      canvas: [
      {
        type: 'line',
        x1: 500,
        y1: 20,
        x2: 0,
        y2: 25,
        lineWidth: 1,
        lineColor: 'black',
      },
    ], margin: [0, 10, 0, 0]},

    { text: "Tickets / Accidents", alignment: 'center', bold: true, margin: [0, 20, 0, 0] },
    { text: "Accident Record for Past 3 Years", alignment: 'center', fontSize: 8, bold: true, margin: [0, 0, 0, 10] },

    { table:
        {
          widths: [ '15%', '*', '*' ],
          body: [
            [
              { text: 'Date', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
              { text: 'Description', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
              { text: '3 Of Injuries / Fatalities', fontSize: 9, bold: true, margin: [0, 1, 0, 0] },
            ],
            ...filterIncidents(data.incidents),
          ],
        },
      margin: [0, 10, 0, 0],
    },

    { text: '', pageBreak: 'before' },

    { text: "Employment Records", alignment: 'center', bold: true, margin: [0, 20, 0, 0] },
    { text: "Note: DOT Requires That Employment for at Least 3 Years and/or Commercial Driving Experience for the Past 10 Years Be Shown", alignment: 'center', fontSize: 8, bold: true, margin: [0, 0, 0, 10] },

    {
      table: {
        widths: ['35%', '65%'],
        body: [
          ...filterEmploymentRecords(data.records).flatMap(record => [
            [
              { text: 'Employer:', bold: true, fontSize: 11 },
              { text: record[0] || '', fontSize: 11 }
            ],
            [
              { text: 'Employed From:', bold: true, fontSize: 11 },
              { text: record[1] || '', fontSize: 11 }
            ],
            [
              { text: 'Employed To:', bold: true, fontSize: 11 },
              { text: record[2] || '', fontSize: 11 }
            ],
            [
              { text: 'Address:', bold: true, fontSize: 11 },
              { text: record[3] || '', fontSize: 11 }
            ],
            [
              { text: 'Phone:', bold: true, fontSize: 11 },
              { text: record[4] || '', fontSize: 11 }
            ],
            [
              { text: 'Supervisor:', bold: true, fontSize: 11 },
              { text: record[5] || '', fontSize: 11 }
            ],
            [
              { text: 'Position:', bold: true, fontSize: 11 },
              { text: record[6] || '', fontSize: 11 }
            ],
            [
              { text: 'Reason for Leaving:', bold: true, fontSize: 11 },
              { text: record[7] || '', fontSize: 11 }
            ],
            ...generateLicenseQuestion(record),
            [
              { text: '', colSpan: 2, margin: [0, 8, 0, 8], border: [false, false, false, false] },
              {}
            ],
          ]),
        ]
      },
      margin: [0, 10, 0, 0]
    },

    { text: '', pageBreak: 'before' },
  ];
}